import { standardQuerySerializer } from './serializers';
import { applyQueryConfig, ServiceHandler } from '../utils';
import { fetchUserParamsMapping } from '../mappings/services/userServiceMapping';

const fetchCurrentUserHandler = ServiceHandler({ url: '/api/auth/v1/me' });
export function fetchCurrentUser() {
  return fetchCurrentUserHandler();
}

const getMeHandler = ServiceHandler({ url: '/api/auth/v1/me' });
export const getMe = (params: Record<string, unknown> = {}) =>
  getMeHandler({ params });

const disableEnableUserPrivilegesHandler = ServiceHandler({
  url: '/api/auth/v1/users/enable/:email',
  method: 'PUT',
});
export const disableEnableUserPrivileges = ({
  email,
  toggle,
  isSecurityConcern,
}: any) =>
  disableEnableUserPrivilegesHandler(email, {
    params: { enable: toggle, isSecurityConcern },
  });

const requestDeleteUserHandler = ServiceHandler({
  url: '/api/auth/v1/users/:userId',
  method: 'delete',
});
export function RequestDeleteUser(userId: any) {
  return requestDeleteUserHandler(userId);
}

const fetchUsersHandler = ServiceHandler({ url: '/api/auth/v1/users' });
//  user releated apis
export function fetchUsers(params: any) {
  const finalParams = applyQueryConfig(fetchUserParamsMapping, params);
  if (Number.isNaN(finalParams.page)) {
    finalParams.page = 0;
  }
  if (Number.isNaN(finalParams.size)) {
    finalParams.size = 25;
  }
  return fetchUsersHandler({ params: finalParams });
}

//  user releated api
export function fetchUsersForLookup() {
  return fetchUsersHandler({ params: { page: 0, size: 1000 } });
}

export const getAllUsers = (params: Record<string, unknown> = {}) =>
  getAllUsersHandler({ params });
const getAllUsersHandler = ServiceHandler({
  url: '/api/auth/v1/users/all',
  paramsSerializer: (p) =>
    standardQuerySerializer(
      applyQueryConfig(
        [
          { path: 'page' },
          { path: 'size' },
          { path: 'search' },
          {
            paramKey: 'sort',
            transform: ({ orderBy, order }: any) => {
              switch (orderBy) {
                case 'accountType':
                case 'name':
                  if (orderBy && order) {
                    return `account.${orderBy},${order}`;
                  }
                  break;
                case 'created':
                case 'modified':
                case 'lastLogin':
                case 'primaryContact':
                case 'mfaType':
                case 'npn':
                case 'firstName':
                case 'lastName':
                case 'email':
                case 'role.name':
                  if (orderBy && order) {
                    return `${orderBy},${order}`;
                  }
                  break;
                default:
                  if (orderBy && order) {
                    return `${orderBy}.keyword,${order}`;
                  }
                  return null;
              }
            },
          },
        ],
        p
      )
    ),
});

export const getAllAgencyTeams = (params: Record<string, unknown> = {}) =>
  getAllAgencyTeamsHandler({ params });
const getAllAgencyTeamsHandler = ServiceHandler({
  url: '/api/auth/v1/teams/agencies',
  paramsSerializer: (p) =>
    standardQuerySerializer(
      applyQueryConfig(
        [
          { path: 'page' },
          { path: 'size' },
          { path: 'search' },
          {
            paramKey: 'sort',
            transform: ({ orderBy, order }: any) => {
              switch (orderBy) {
                case 'account':
                  if (orderBy && order) {
                    return `${orderBy}.name,${order}`;
                  }
                  break;
                case 'modified':
                  return null;
                default:
                  if (orderBy && order) {
                    return `${orderBy},${order}`;
                  }
                  return null;
              }
            },
          },
        ],
        p
      )
    ),
});

/**
 * @deprecated
 * @replacedBy "getUsers" in /src/api/users/users.api.ts
 */
export const getUsers = (params: Record<string, unknown> = {}) =>
  getUsersHandler({ params });
const getUsersHandler = ServiceHandler({
  url: '/api/auth/v1/users',
  paramsSerializer: (p) =>
    standardQuerySerializer(
      applyQueryConfig(
        [
          { path: 'page' },
          { path: 'size' },
          { path: 'search' },
          {
            paramKey: 'sort',
            transform: ({ orderBy, order }: any) => {
              switch (orderBy) {
                case 'created':
                case 'modified':
                case 'lastLogin':
                case 'primaryContact':
                case 'mfaType':
                case 'npn':
                case 'firstName':
                case 'lastName':
                case 'email':
                case 'slackHandle':
                case 'role.name':
                case 'passwordResetDate':
                case 'enabled':
                  if (orderBy && order) {
                    return `${orderBy},${order}`;
                  }
                  break;
                default:
                  if (orderBy && order) {
                    return `${orderBy}.keyword,${order}`;
                  }
                  return null;
              }
            },
          },
        ],
        p
      )
    ),
});

const countUndefinedRolesHandler = ServiceHandler({
  url: '/api/auth/v1/users/unassigned',
});
export function countUndefinedRoles() {
  return countUndefinedRolesHandler();
}

const requestUserHandler = ServiceHandler({
  url: '/api/auth/v1/users/:userId',
});
export function requestUser(userId: any) {
  return requestUserHandler(userId);
}

const createUserHandler = ServiceHandler({
  url: '/api/auth/v1/users',
  method: 'post',
});
export function createUser(user: any) {
  return createUserHandler({ data: user });
}

const updateUserHandler = ServiceHandler({
  url: '/api/auth/v1/users/:userId',
  method: 'put',
});
export function updateUser(userId: any, user: any) {
  return updateUserHandler(userId, { data: user });
}

export const updateAgencyUser = ServiceHandler({
  url: '/api/auth/v1/users/:userId',
  method: 'PUT',
});

const rolesHandler = ServiceHandler({ url: '/api/auth/v1/roles' });
/**
 * Fetches all roles for dropdowns and selection lists.
 * Uses the authenticated user's account type automatically from JWT token.
 * Returns paginated response - use data.content to access roles array.
 *
 * Note: The API automatically uses the account type from the authenticated user's token,
 * so no accountType parameter is needed.
 */
export function fetchRoles() {
  return rolesHandler({
    params: { size: '1000', sortBy: 'name', sortOrder: 'asc' },
  });
}

const fetchRolesByAccountTypeHandler = ServiceHandler({
  url: '/api/auth/v1/roles/accounttype/:accountType',
});
/**
 * Fetches roles for a specific account type.
 * Use this when you need roles for an account type different from the authenticated user's type.
 * Returns paginated response - use data.content to access roles array.
 *
 * @param accountType - The account type to fetch roles for (e.g., 'AGENCY', 'CARRIER', 'CUSTOMER')
 */
export function fetchRolesByAccountType(accountType: string) {
  return fetchRolesByAccountTypeHandler(accountType, {
    params: { size: '1000', sortBy: 'name', sortOrder: 'asc' },
  });
}

/**
 * Fetches roles with pagination, sorting, and search support.
 * Uses the authenticated user's account type automatically.
 * Supports: page, size, sortBy, sortOrder, search, systemRole, realm
 */
export function fetchAllRoles(queryParams?: string) {
  return rolesHandler({
    url: `/api/auth/v1/roles${queryParams ? `?${queryParams}` : ''}`,
  });
}

const fetchTeamsHandler = ServiceHandler({ url: '/api/auth/v1/teams/all' });
export function fetchTeams() {
  return fetchTeamsHandler();
}

const tamsHandler = ServiceHandler({ url: '/api/auth/v1/users/tams' });
export function fetchTams() {
  return tamsHandler();
}
export const fetchTamsV2 = (params: Record<string, unknown> = {}) =>
  tamsHandler({ params });

const bdsHandler = ServiceHandler({ url: '/api/auth/v1/users/bds' });
export function fetchBds() {
  return bdsHandler();
}

/**
 * Active Cowbell users, unfiltered by role. Backs the agency primary/secondary
 * Cowbell contact pickers, which are no longer restricted to TAM/Sales
 * Underwriter or BDS/AE. Use fetchTams/fetchBds where a genuine TAM or BDS is
 * required, such as assigning one.
 */
const cowbellersHandler = ServiceHandler({
  url: '/api/auth/v1/users/cowbellers',
});
export const fetchCowbellers = (params: Record<string, unknown> = {}) =>
  cowbellersHandler({ params });

const assignUserHandler = ServiceHandler({
  url: '/api/auth/v1/users/assign/:userId',
  method: 'put',
});
export function assignUser(userId: any, accountIds: any) {
  return assignUserHandler(userId, { data: accountIds });
}

const resendInviteHandler = ServiceHandler({
  url: '/api/auth/v1/resendUserInvitationEmail ',
  method: 'post',
});
export function resendInvite(userDetails: any) {
  return resendInviteHandler({ data: userDetails });
}

const addExternalAccountsHandler = ServiceHandler({
  url: '/api/auth/v1/users/add-accounts/:userEmail',
  method: 'put',
});
export function addExternalAccounts(userEmail: any, payload: any) {
  return addExternalAccountsHandler(userEmail, { data: payload });
}

const getUserAccountsByIdHandler = ServiceHandler({
  url: '/api/auth/v1/users/get-accounts/:userEmail',
});
export function getUserAccountsById(userEmail: any) {
  return getUserAccountsByIdHandler(userEmail);
}

const getMyAccountsHandler = ServiceHandler({
  url: '/api/auth/v1/users/my-accounts',
});
export function getMyAccounts() {
  return getMyAccountsHandler();
}

const getAccountTempTokenHandler = ServiceHandler({
  url: '/api/auth/v1/accounts/:accountId/myToken',
  method: 'post',
});
export function getAccountTempToken(accountId: any) {
  return getAccountTempTokenHandler(accountId);
}

const getAgentsHandler = ServiceHandler({
  url: '/api/auth/v1/users/fellow-users',
});
export const getAgents = (params: Record<string, unknown> = {}) =>
  getAgentsHandler({ params });

const getAgentsInAdminHandler = ServiceHandler({
  url: '/api/auth/v1/users/agency',
});
export const getAgentsInAdmin = (params: Record<string, unknown> = {}) =>
  getAgentsInAdminHandler({ params });

const getAccountExecutivesHandler = ServiceHandler({
  url: `/api/auth/v1/users/aes?size=100`,
});
export const getAccountExecutives = (params: Record<string, unknown> = {}) =>
  getAccountExecutivesHandler({ params });

const getAllUnderWritersHandler = ServiceHandler({
  url: `/api/auth/v1/users/uw?size=100`,
});
export const getAllUnderWriters = (params: Record<string, unknown> = {}) =>
  getAllUnderWritersHandler({ params });

const getAllZurichAndCowbellUnderWritersHandler = ServiceHandler({
  url: `/api/auth/v1/users/zurich_cowbell/uw?size=100`,
});
export const getAllZurichAndCowbellUnderWriters = (
  params: Record<string, unknown> = {}
) => getAllZurichAndCowbellUnderWritersHandler({ params });

const getAllPlatformAgentsHandler = ServiceHandler({
  url: ` /api/auth/v1/users/agents`,
});
export const getAllPlatformAgents = (params: Record<string, unknown> = {}) =>
  getAllPlatformAgentsHandler({ params });

const getAllAgentAgenciesHandler = ServiceHandler({
  url: `/api/auth/v1/users/children?`,
  paramsSerializer: (p) =>
    standardQuerySerializer(applyQueryConfig([{ path: 'username' }], p)),
});
export const getAllAgentAgencies = (params: Record<string, unknown> = {}) =>
  getAllAgentAgenciesHandler({ params });

const getCowbellAccountDetailsHandler = ServiceHandler({ url: `/api/auth/v1` });
export const getCowbellAccountDetails = (
  params: Record<string, unknown> = {}
) => getCowbellAccountDetailsHandler({ params });

/**
 * Authority levels — the tiers the business offers, per domain.
 *
 * Rejects rather than resolves against a login that predates authority levels, which is how the
 * UI decides not to render the fields at all. There is no version check anywhere: the console
 * shows exactly what the server says exists.
 */
const getOfferedAuthorityLevelsHandler = ServiceHandler({
  url: '/api/auth/v1/users/authority-levels',
});
export const getOfferedAuthorityLevels = () =>
  getOfferedAuthorityLevelsHandler({});

/** A single user's level per domain, plus the domains their role makes applicable. */
const getUserAuthorityLevelsHandler = ServiceHandler({
  url: '/api/auth/v1/users/:userId/authority-levels',
});
export const getUserAuthorityLevels = (userId: string) =>
  getUserAuthorityLevelsHandler(userId);

/**
 * Addresses of colleagues who hold enough authority to approve at a level.
 *
 * <p>The level and an optional search term are all that cross the wire, and only a name and an
 * address come back. The alternative — listing the account and posting every user id to
 * `authority-levels/by-user` — put a roster of ids in a query string, where it lands in access
 * logs, proxy logs and browser history, for a question the database answers in one join.
 *
 * <p>Cowbell-only at the server, which is also the only band claims authority is issued in.
 */
const getAuthorityApproversHandler = ServiceHandler({
  url: '/api/auth/v1/users/authority-levels/approvers',
});
export const getAuthorityApprovers = (
  domain: string,
  minLevel: number,
  search: string
) => getAuthorityApproversHandler({ params: { domain, minLevel, search } });

/**
 * Levels for a page of users at once, so a table costs one request rather than one per row.
 */
const getAuthorityLevelsByUserHandler = ServiceHandler({
  url: '/api/auth/v1/users/authority-levels/by-user',
});
export const getAuthorityLevelsByUser = (userIds: string[]) =>
  getAuthorityLevelsByUserHandler({ params: { userIds: userIds.join(',') } });
